import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import { TechRadarPage } from '@backstage-community/plugin-tech-radar';
import {
	CatalogEntityPage,
	CatalogIndexPage,
	catalogPlugin,
} from '@backstage/plugin-catalog';
import {
	CatalogImportPage,
	catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import { azureDevOpsPlugin, AzurePullRequestsPage } from '@backstage/plugin-azure-devops';
import {
	TechDocsIndexPage,
	techdocsPlugin,
	TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { AzurePipelineRunsPage } from './components/AzurePipelineRunsPage';
import { AzureTodayPullRequestsPage } from './components/AzureTodayPullRequestsPage';
import { entityPage } from './components/catalog/EntityPage';
import { DefectDojoPage } from './components/DefectDojoPage';
import { ZendeskConnectPage } from './components/ZendeskConnectPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
	AlertDisplay,
	OAuthRequestDialog,
	SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { microsoftAuthApiRef } from '@backstage/core-plugin-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { CostInsightsPage } from '@backstage/plugin-cost-insights';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { PagerDutyPage } from '@pagerduty/backstage-plugin';
import { grafanaPlugin } from '@marble-sh/backstage-plugin-grafana';
import { UnifiedThemeProvider } from '@backstage/theme';
import { zestTheme } from './theme/zestTheme';
import { TestReportGeneratorPage } from '@internal/plugin-test-report-generator';
import { ApiStatusPage } from './components/ApiStatusPage';

const app = createApp({
	themes: [
		{
			id: 'zest-light',
			title: 'Zest',
			variant: 'light',
			Provider: ({ children }) => (
				<UnifiedThemeProvider theme={zestTheme}>
					{children}
				</UnifiedThemeProvider>
			),
		},
	],
	plugins: [
		grafanaPlugin,
		catalogPlugin,
		catalogImportPlugin,
		scaffolderPlugin,
		apiDocsPlugin,
		techdocsPlugin,
		orgPlugin,
		azureDevOpsPlugin,
	],
	apis,
	bindRoutes({ bind }) {
		bind(catalogPlugin.externalRoutes, {
			createComponent: scaffolderPlugin.routes.root,
			viewTechDoc: techdocsPlugin.routes.docRoot,
			createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
		});
		bind(apiDocsPlugin.externalRoutes, {
			registerApi: catalogImportPlugin.routes.importPage,
		});
		bind(scaffolderPlugin.externalRoutes, {
			registerComponent: catalogImportPlugin.routes.importPage,
			viewTechDoc: techdocsPlugin.routes.docRoot,
		});
		bind(orgPlugin.externalRoutes, {
			catalogIndex: catalogPlugin.routes.catalogIndex,
		});
	},
	...(process.env.NODE_ENV === 'development'
		? {}
		: {
				components: {
					SignInPage: props => (
						<SignInPage
							{...props}
							auto
							provider={{
								id: 'microsoft',
								title: 'Microsoft',
								message: 'Sign in with Microsoft',
								apiRef: microsoftAuthApiRef,
							}}
						/>
					),
				},
		  }),
});

const routes = (
	<FlatRoutes>
		<Route path="/" element={<Navigate to="catalog" />} />
		<Route path="/catalog" element={<CatalogIndexPage />} />
		<Route
			path="/catalog/:namespace/:kind/:name"
			element={<CatalogEntityPage />}
		>
			{entityPage}
		</Route>
		<Route path="/docs" element={<TechDocsIndexPage />} />
		<Route
			path="/docs/:namespace/:kind/:name/*"
			element={<TechDocsReaderPage />}
		>
			<TechDocsAddons>
				<ReportIssue />
			</TechDocsAddons>
		</Route>
		<Route path="/create" element={<ScaffolderPage />} />
		<Route path="/zendesk/connect" element={<ZendeskConnectPage />} />
		<Route path="/api-docs" element={<ApiExplorerPage />} />
		<Route
			path="/catalog-import"
			element={
				<RequirePermission permission={catalogEntityCreatePermission}>
					<CatalogImportPage />
				</RequirePermission>
			}
		/>
		<Route path="/search" element={<SearchPage />}>
			{searchPage}
		</Route>
		<Route path="/settings" element={<UserSettingsPage />} />
		<Route path="/catalog-graph" element={<CatalogGraphPage />} />
		<Route
			path="/tech-radar"
			element={<TechRadarPage width={1500} height={900} />}
		/>
		<Route path="/cost-insights" element={<CostInsightsPage />} />
		<Route path="/pagerduty" element={<PagerDutyPage />} />
		<Route path="/notifications" element={<NotificationsPage />} />
		<Route
			path="/azure-pull-requests"
			element={<AzurePullRequestsPage projectName="Zest Development" />}
		/>
		<Route
			path="/azure-pull-requests-today"
			element={<AzureTodayPullRequestsPage />}
		/>
		<Route path="/azure-pipeline-runs" element={<AzurePipelineRunsPage />} />
		<Route path="/defectdojo" element={<DefectDojoPage />} />
		<Route path="/test-reports" element={<TestReportGeneratorPage />} />
		<Route path="/api-status" element={<ApiStatusPage />} />
		<Route path="/release-insights" element={<TestReportGeneratorPage />} />
		<Route path="/testing-effort" element={<TestReportGeneratorPage />} />
		<Route path="/release-reports" element={<TestReportGeneratorPage />} />
	</FlatRoutes>
);

export default app.createRoot(
	<>
		<AlertDisplay />
		<OAuthRequestDialog />
		<SignalsDisplay />
		<AppRouter>
			<Root>{routes}</Root>
		</AppRouter>
	</>,
);
